/**
 * Opportunities — deals with a forecastable value and a close date.
 *
 * Weighted value (value × probability) is the number a sales manager
 * actually forecasts on, so it is shown rather than leaving the reader to
 * multiply two columns in their head.
 *
 * Every amount is in its row's own currency. The stat cards read
 * GET /v1/crm/opportunities/summary, which sums every open deal per currency:
 * the headline in the workspace currency, the others beneath it, never added
 * in (CUR-2; they used to add the first page of rows and label it "$").
 */
import { StatCard } from "@/components/ui";
import { MoneyStatCard } from "@/components/Money";
import { QueryError } from "@/components/QueryError";
import { useCurrencyCreateField } from "@/components/CurrencyField";
import { ResourceListPage } from "@/components/ResourceListPage";
import { useApiQuery } from "@/lib/api/hooks";
import { percentOf } from "@/lib/currency";
import { formatDate, formatMoney } from "@/lib/money";
import type { OpportunityRow, OpportunitySummary } from "./types";

export function CrmOpportunitiesPage() {
  const pipeline = useApiQuery<OpportunitySummary>(
    ["crm", "opportunities", "summary"],
    "/crm/opportunities/summary",
  );
  const summary = pipeline.data;
  const currencyField = useCurrencyCreateField();

  return (
    <ResourceListPage<OpportunityRow>
      title="Opportunities"
      subtitle="Deals with a value and a close date. Weighted value is what the forecast is built on."
      breadcrumb={["CRM", "Opportunities"]}
      path="/crm/opportunities"
      queryKey={["crm", "opportunities"]}
      createLabel="New opportunity"
      emptyTitle="No opportunities"
      emptyMessage="An opportunity is a lead that has become a real deal — it has a value, a probability and a date you expect it to close."
      createFields={[
        {
          field: "name",
          label: "Opportunity name",
          required: true,
          placeholder: "Acme — annual licence renewal",
        },
        {
          field: "expectedValue",
          label: "Expected value",
          type: "number",
          hint: "Deal size, in the currency below.",
        },
        currencyField,
        {
          field: "probabilityPercent",
          label: "Probability %",
          type: "number",
          hint: "0–100. Drives the weighted forecast.",
        },
        {
          field: "expectedCloseDate",
          label: "Expected close date",
          type: "date",
        },
      ]}
      toBody={(form) => ({
        name: form.name,
        expectedValue: form.expectedValue || "0",
        // Empty: the API applies the workspace currency itself (R3).
        currencyCode: form.currencyCode || undefined,
        // The API type-checks this one as a number, not a string.
        probabilityPercent: Number(form.probabilityPercent || 0),
        expectedCloseDate: form.expectedCloseDate || undefined,
      })}
      columns={[
        { key: "name", label: "Opportunity" },
        { key: "stage", label: "Stage" },
        {
          key: "expected_value",
          label: "Value",
          numeric: true,
          render: (r) =>
            r.expected_value
              ? formatMoney(r.expected_value, r.currency_code)
              : "—",
        },
        {
          key: "probability_percent",
          label: "Probability",
          numeric: true,
          render: (r) =>
            r.probability_percent === null ? "—" : `${r.probability_percent}%`,
        },
        {
          key: "weighted",
          label: "Weighted",
          numeric: true,
          // Exact: value × probability / 100 on the decimal string.
          render: (r) =>
            formatMoney(
              percentOf(r.expected_value ?? "0", r.probability_percent ?? 0),
              r.currency_code,
            ),
        },
        {
          key: "expected_close_date",
          label: "Expected close",
          render: (r) =>
            r.expected_close_date ? formatDate(r.expected_close_date) : "—",
        },
      ]}
    >
      {pipeline.isError && (
        <QueryError
          error={pipeline.error}
          retrying={pipeline.isFetching}
          onRetry={() => void pipeline.refetch()}
        />
      )}
      {summary && (
        <div className="atlas-grid atlas-grid--3 mb-4">
          <StatCard label="Open opportunities" value={summary.count} />
          <MoneyStatCard label="Pipeline value" figure={summary.pipeline} />
          <MoneyStatCard
            label="Weighted forecast"
            figure={summary.weighted}
            meta="Value × probability"
          />
        </div>
      )}
    </ResourceListPage>
  );
}
